import React from 'react'
import {
    Box, Button, Checkbox, CircularProgress, InputAdornment, LinearProgress, Table, TableBody, TableCell,
    TableContainer, TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined'
import SearchIcon from '@mui/icons-material/Search'
import { toast } from 'react-toastify'
import { useNavigate, useParams } from 'react-router-dom'
import {
    ApiPushAddGroupMembers, ApiPushGetGroup, ApiPushGetGroupMembers, ApiPushRemoveGroupMembers,
} from '../../apiService/Core/Apicall'
import PopupDialog from '../../Components/Popups/ConfirmDialog'
import DarkDialog from '../../Components/Push/DarkDialog'
import PushDisabledNotice, { InlineAlert } from '../../Components/Push/PushDisabledNotice'
import PushPageHeader from '../../Components/Push/PushPageHeader'
import PushPanel from '../../Components/Push/PushPanel'
import UserPicker from '../../Components/Push/UserPicker'
import { useGuardedFetch } from '../../Components/Push/useGuardedFetch'
import {
    checkboxSx, dangerBtnSx, headerBtnSx, paginationStyle, searchFieldStyle, tableSx, tdStyle, thStyle, trHover,
} from '../../Components/Push/pushStyles'
import {
    chunk, fmtDate, getErrorMessage, GROUP_MEMBERS_PER_REQUEST, isPushDisabled, qs, useDebouncedValue,
} from '../../Components/Push/pushUtils'
import type { GroupMember, Pagination, UserLite } from '../../Components/Push/types'

const HEADERS = ['Name', 'Email', 'Added at']

const GroupDetailView: React.FC<{ id: string }> = ({ id }) => {
    const navigate = useNavigate()
    const [search, setSearch] = React.useState('')
    const q = useDebouncedValue(search.trim())
    const [page, setPage] = React.useState(0)
    const [rowsPerPage, setRowsPerPage] = React.useState(20)
    // selection survives paging and searching
    const [selection, setSelection] = React.useState<string[]>([])
    const [addOpen, setAddOpen] = React.useState(false)
    const [toAdd, setToAdd] = React.useState<UserLite[]>([])
    const [adding, setAdding] = React.useState(false)
    const [removeOpen, setRemoveOpen] = React.useState(false)
    const [removing, setRemoving] = React.useState(false)
    const busyRef = React.useRef(false)

    const groupFetcher = React.useCallback((silent: boolean) => ApiPushGetGroup(id, { silent }), [id])
    const { data: groupData, error: groupError, refetch: refetchGroup } = useGuardedFetch<any>(groupFetcher)
    const membersFetcher = React.useCallback(
        (silent: boolean) => ApiPushGetGroupMembers(id, qs({ q, page: page + 1, limit: rowsPerPage }), { silent }),
        [id, q, page, rowsPerPage]
    )
    const { data, error, loading, refetch } = useGuardedFetch<any>(membersFetcher, { silentLoads: true })

    const group = groupData?.data
    const rows: GroupMember[] = data?.data?.items ?? []
    const pagination: Pagination | undefined = data?.meta?.pagination
    const total = pagination?.total ?? 0

    React.useEffect(() => {
        if (data && page > 0 && rows.length === 0) setPage(Math.max(0, Math.ceil(total / rowsPerPage) - 1))
    }, [data, page, rows.length, total, rowsPerPage])

    const reload = () => { refetch(); refetchGroup() }

    const pageIds = rows.map((r) => r.userId)
    const allOnPage = pageIds.length > 0 && pageIds.every((u) => selection.includes(u))
    const someOnPage = pageIds.some((u) => selection.includes(u))
    const toggleOne = (uid: string) =>
        setSelection((s) => (s.includes(uid) ? s.filter((x) => x !== uid) : [...s, uid]))
    const togglePage = () =>
        setSelection((s) => (allOnPage ? s.filter((x) => !pageIds.includes(x)) : Array.from(new Set([...s, ...pageIds]))))

    const onAdd = async () => {
        if (busyRef.current || toAdd.length === 0) return
        busyRef.current = true
        setAdding(true)
        try {
            let added = 0
            let already = 0
            // the API takes up to GROUP_MEMBERS_PER_REQUEST ids; adding is idempotent so a partial failure can simply be repeated
            for (const ids of chunk(toAdd.map((u) => u.id), GROUP_MEMBERS_PER_REQUEST)) {
                const res = await ApiPushAddGroupMembers(id, ids)
                added += res?.data?.added ?? 0
                already += res?.data?.alreadyMembers ?? 0
            }
            toast.success(`Added ${added}, already members ${already}`)
            setAddOpen(false)
            setToAdd([])
            reload()
        } catch (e) {
            toast.error(getErrorMessage(e))
            reload()
        } finally {
            busyRef.current = false
            setAdding(false)
        }
    }

    const onRemove = async () => {
        if (busyRef.current || selection.length === 0) return
        busyRef.current = true
        setRemoving(true)
        try {
            let removed = 0
            for (const ids of chunk(selection, GROUP_MEMBERS_PER_REQUEST)) {
                const res = await ApiPushRemoveGroupMembers(id, ids)
                removed += res?.data?.removed ?? 0
            }
            toast.success(`Removed ${removed} member${removed === 1 ? '' : 's'}`)
            setSelection([])
            setRemoveOpen(false)
            reload()
        } catch (e) {
            toast.error(getErrorMessage(e))
            setRemoveOpen(false)
            reload()
        } finally {
            busyRef.current = false
            setRemoving(false)
        }
    }

    const back = (
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/groups')} sx={headerBtnSx}>
            Back
        </Button>
    )

    if (isPushDisabled(groupError) || isPushDisabled(error)) {
        return (
            <div className="common-layout flex-column">
                <PushPageHeader title="Group" actions={back} />
                <PushDisabledNotice />
            </div>
        )
    }
    if (groupError) {
        return (
            <div className="common-layout flex-column">
                <PushPageHeader title="Group" actions={back} />
                <InlineAlert severity="error">{getErrorMessage(groupError)}</InlineAlert>
            </div>
        )
    }

    return (
        <div className="common-layout flex-column">
            <PushPageHeader
                title={group ? group.name : 'Group'}
                subtitle={
                    <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', mt: 0.5 }}>
                        {group ? `${group.memberCount ?? 0} members${group.description ? ` - ${group.description}` : ''}` : ''}
                    </Typography>
                }
                actions={
                    <>
                        {back}
                        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setAddOpen(true)} sx={headerBtnSx}>
                            Add members
                        </Button>
                    </>
                }
            />

            <PushPanel
                title="Members"
                icon={<GroupsOutlinedIcon sx={{ color: '#ff6a3d', fontSize: 20 }} />}
                sx={{ mt: 2 }}
                bodySx={{ p: 0 }}
                action={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Button
                            variant="contained"
                            disabled={selection.length === 0 || removing}
                            startIcon={<DeleteOutlineIcon />}
                            onClick={() => setRemoveOpen(true)}
                            sx={dangerBtnSx}
                        >
                            Remove selected ({selection.length})
                        </Button>
                        <TextField
                            placeholder="Search members by name or email"
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(0) }}
                            size="small"
                            sx={{ ...searchFieldStyle, width: 300 }}
                            slotProps={{ input: { startAdornment: (<InputAdornment position="start"><SearchIcon className="color-primary" /></InputAdornment>) } }}
                        />
                    </Box>
                }
            >
                {error && <Box sx={{ p: 2 }}><InlineAlert severity="error">{getErrorMessage(error)}</InlineAlert></Box>}
                <Box sx={{ height: 3 }}>{loading && <LinearProgress sx={{ height: 3, background: 'transparent', '& .MuiLinearProgress-bar': { background: '#ff6a3d' } }} />}</Box>
                <TableContainer sx={{ maxHeight: '60vh' }}>
                    <Table stickyHeader sx={tableSx}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ ...thStyle, width: 48 }} padding="checkbox">
                                    <Checkbox checked={allOnPage} indeterminate={!allOnPage && someOnPage} onChange={togglePage} disabled={pageIds.length === 0} sx={checkboxSx} />
                                </TableCell>
                                {HEADERS.map((h) => <TableCell key={h} sx={thStyle}>{h}</TableCell>)}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.length > 0 ? rows.map((m) => (
                                <TableRow key={m.userId} sx={trHover} hover selected={selection.includes(m.userId)}>
                                    <TableCell padding="checkbox" sx={tdStyle}>
                                        <Checkbox checked={selection.includes(m.userId)} onChange={() => toggleOne(m.userId)} sx={checkboxSx} />
                                    </TableCell>
                                    <TableCell sx={tdStyle}>
                                        <Typography fontSize={13} fontWeight={600} color="#e0e0e0">{m.name || '-'}</Typography>
                                    </TableCell>
                                    <TableCell sx={tdStyle}>{m.email || '-'}</TableCell>
                                    <TableCell sx={tdStyle}>{fmtDate(m.addedAt)}</TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={HEADERS.length + 1} align="center" sx={{ py: 8, background: '#1a1a1a', border: 'none' }}>
                                        <PersonAddAltOutlinedIcon sx={{ color: '#2f2f2f', fontSize: 40, mb: 1 }} />
                                        <Typography color="#4a4a4a" fontSize={13}>
                                            {loading ? 'Loading members...' : 'This group has no members.'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div" count={total} page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
                    rowsPerPageOptions={[10, 20, 50, 100]} showFirstButton showLastButton
                    sx={paginationStyle}
                />
            </PushPanel>

            <DarkDialog
                open={addOpen}
                onClose={() => setAddOpen(false)}
                title="Add members"
                icon={<PersonAddAltOutlinedIcon sx={{ fontSize: 18 }} />}
                busy={adding}
                confirmText={`Add ${toAdd.length || ''}`.trim()}
                onConfirm={onAdd}
                confirmDisabled={toAdd.length === 0}
            >
                <UserPicker selected={toAdd} onChange={setToAdd} />
                {adding && <Box sx={{ mt: 2, textAlign: 'center' }}><CircularProgress size={18} sx={{ color: '#ff6a3d' }} /></Box>}
            </DarkDialog>

            <PopupDialog
                open={removeOpen}
                onClose={() => setRemoveOpen(false)}
                onConfirm={onRemove}
                loading={removing}
                variant="danger"
                title="Remove Members"
                message={`Remove ${selection.length} selected member${selection.length === 1 ? '' : 's'} from this group? The users themselves are not deleted.`}
                confirmText="Remove"
            />
        </div>
    )
}

const GroupDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>()
    if (!id) return null
    return <GroupDetailView key={id} id={id} />
}

export default GroupDetail
